import { useContext, useState } from 'react';
import { useIntl } from 'react-intl';

import { Modal } from '@calm-web/design-system';

import PaymentElementsForm from '@/components/purchase/PaymentElementsForm';
import PurchaseForm from '@/components/purchase/PurchaseForm';
import { useStripeElements } from '@/components/purchase/StripeElementsProvider';
import { subscribePurchaseErrors } from '@/components/purchase/utils/Purchase';
import { useAnalytics } from '@/hooks/analytics';
import { useSubPurchase } from '@/hooks/purchase/useSubPurchase';
import { usePurchaseParamsState } from '@/hooks/store';

import { PreSignupFlowContext } from '../../PreSignupProvider';
import PaymentDetailsModal from '../PaymentDetailsModal';
import PaymentFormHeading from '../PaymentFormHeading';
import messages from './messages';
import { Cta, Divider, PaymentTitleHeading, PaymentTitleWrapper, Wrapper } from './styles';
import { PaymentFormModuleProps } from './types';

const PaymentFormModule = ({
	buttonBgColor = 'buttonBlue',
	hideHeaderCost,
	onPurchase,
	onSuccessOverride,
	isFreeTrial = true,
	hideDivider = false,
	hideAlternativePaymentMethods = false,
	hideApplePay = false,
	hidePaypal = false,
	isSecondFreeTrial = false,
	isVisible = false,
}: PaymentFormModuleProps) => {
	const { formatMessage } = useIntl();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { logEvent } = useAnalytics();
	const { isStripeNextEnabled } = useStripeElements();
	const { nextScreen } = useContext(PreSignupFlowContext);
	const purchaseParams = usePurchaseParamsState();

	const submitSubPurchase = useSubPurchase();

	function onTooltipClick(): void {
		setIsModalOpen(true);
		logEvent({
			eventName: 'Subscribe : Purchase : Form : Tool Tip : Clicked',
			eventProps: {
				tool_tip: 'why now',
			},
		});
	}

	function onSuccess() {
		if (onSuccessOverride) {
			onSuccessOverride();
			return;
		}
		nextScreen();
	}

	if (!isVisible) {
		return null;
	}

	return (
		<Wrapper role="alert" aria-live="polite">
			{!hideHeaderCost && <PaymentFormHeading isSecondFreeTrial={isSecondFreeTrial} />}
			{!hideDivider && <Divider />}
			<PaymentTitleWrapper>
				<PaymentTitleHeading>{formatMessage(messages.heading)}</PaymentTitleHeading>
				{isFreeTrial && purchaseParams.plan !== 'monthly' && (
					<Cta onClick={onTooltipClick}>{formatMessage(messages.modalCta)}</Cta>
				)}
			</PaymentTitleWrapper>
			<Modal
				isOpen={isModalOpen}
				closeModal={(): void => setIsModalOpen(false)}
				canClose
				aria-label={formatMessage(messages.modalTitle)}
			>
				<PaymentDetailsModal onCloseClick={(): void => setIsModalOpen(false)} />
			</Modal>
			{isStripeNextEnabled ? (
				<PaymentElementsForm
					hidePaypal={hidePaypal}
					endpoint="subscription"
					afterPurchaseSuccess={onSuccess}
					subscribePurchaseErrors={subscribePurchaseErrors.filter(v => v !== 'previously-trialed')}
				/>
			) : (
				<PurchaseForm
					isSecondFreeTrial={isSecondFreeTrial}
					buttonBgColor={buttonBgColor}
					onSuccess={onSuccess}
					onPurchase={onPurchase || submitSubPurchase}
					hideAlternativePaymentMethods={hideAlternativePaymentMethods}
					hideApplePay={hideApplePay}
					hidePaypal={hidePaypal}
					isMonthlyPricingTest={false}
				/>
			)}
		</Wrapper>
	);
};

export default PaymentFormModule;
